import { TemplatableActivityDateRange } from '@/apis/activityDateRange';
import { AccordionField } from '@components/DrawerAccordion';
import TemplateCapableFetchAutocomplete from '@/components/TemplateCapableFetchAutocomplete';
import TemplateCapableTextField from '@pages/AdvancedSearchListCreatorPage/customWidgets/templateCapableComponents/TemplateCapableTextField';
import { SearchTemplatePageContext } from '@/context/SearchTemplateContext';
import { useContext } from 'react';
import { AutocompleteResult, isTemplatePlaceholder, TemplatePlaceholder } from '@/types';
import {
  ACTIVITY_OPTIONS_WITH_IDS,
  ACTIVITY_STATUSES_WITH_IDS,
} from '@/constants/filterCriteria/engagement';
import ActivityDateRangePicker from './ActivityDateRangePicker';

type EngagementFormProps = {
  onActivityChange: (newValue: AutocompleteResult[] | TemplatePlaceholder) => void;
  activityValue: AutocompleteResult[] | TemplatePlaceholder;
  onActivityStatusChange: (newValue: AutocompleteResult[] | TemplatePlaceholder) => void;
  activityStatus: AutocompleteResult[] | TemplatePlaceholder;
  onActivityDateChange: (newValue: TemplatableActivityDateRange) => void;
  activityDateValue: TemplatableActivityDateRange;
  setIsFilterInvalid: (isInvalid: boolean) => void;
  isAdvancedSearch?: boolean;
  subject: string | TemplatePlaceholder;
  onSubjectChange: (value: string | TemplatePlaceholder) => void;
};

const EngagementForm = ({
  activityValue = [] as AutocompleteResult[],
  onActivityChange = () => {},
  activityStatus = [] as AutocompleteResult[],
  onActivityStatusChange = () => {},
  setIsFilterInvalid = () => {},
  onActivityDateChange = () => {},
  activityDateValue = {} as TemplatableActivityDateRange,
  isAdvancedSearch = false,
  subject = '',
  onSubjectChange = () => {},
}: EngagementFormProps) => {
  const inSearchTemplate = useContext(SearchTemplatePageContext);

  return (
    <>
      <AccordionField title="Activity">
        <TemplateCapableFetchAutocomplete
          sx={isAdvancedSearch ? { width: '50ch' } : {}}
          minLength={0}
          label="Add an activity"
          preLoadedOptions={ACTIVITY_OPTIONS_WITH_IDS}
          value={activityValue}
          onChange={(value) => onActivityChange(value)}
          loadingText="Looking for activities..."
          noOptionsText="No activities found"
          isOnTemplatePage={inSearchTemplate}
          templateBuilderDisplayName="Engagement history filter: Activity"
        />
      </AccordionField>

      {!isTemplatePlaceholder(activityValue) && activityValue.length === 0 && (
        <AccordionField
          title="
        Select at least one activity to see more engagement options"
        />
      )}
      {(isTemplatePlaceholder(activityValue) || activityValue.length !== 0) && (
        <>
          <AccordionField title="Engagement options for the selected activity(ies)" />

          <AccordionField
            title={
              isTemplatePlaceholder(activityStatus) || activityStatus.length
                ? 'Activity status'
                : 'Activity status: Any'
            }
          >
            <TemplateCapableFetchAutocomplete
              minLength={0}
              label="Add an activity status"
              preLoadedOptions={ACTIVITY_STATUSES_WITH_IDS}
              value={activityStatus}
              onChange={(value) => onActivityStatusChange(value)}
              loadingText="Looking for activity statuses..."
              noOptionsText="No activity statuses found"
              sx={isAdvancedSearch ? { width: '50ch' } : {}}
              isOnTemplatePage={inSearchTemplate}
              templateBuilderDisplayName="Engagement history filter: Activity status"
            />
          </AccordionField>
          <ActivityDateRangePicker
            setIsFilterInvalid={setIsFilterInvalid}
            onChange={onActivityDateChange}
            value={activityDateValue}
            isAdvancedSearch={isAdvancedSearch}
          />

          <AccordionField title={'With a subject beginning with (optional)'}>
            <TemplateCapableTextField
              id="subject"
              label="Enter text to search for"
              variant="filled"
              fullWidth
              value={subject ?? ''}
              onChange={(newVal) => onSubjectChange(newVal)}
              isOnTemplatePage={inSearchTemplate}
              templateBuilderDisplayName="Engagement history filter: Subject"
              sx={isAdvancedSearch ? { width: '544px !important' } : {}}
            />
          </AccordionField>
        </>
      )}
    </>
  );
};

export default EngagementForm;
